import { RefObject, useEffect } from 'react';

interface UseOutsideClickOptions {
  ref: RefObject<HTMLElement | null>;
  handler: (event: PointerEvent) => void;
}

/**
 * Chakra v2's `useOutsideClick` was removed outright in v3 with no
 * replacement export. This is a small, direct re-implementation matching its
 * real behavior: listens for `pointerdown` (fires before `click`, so a
 * dismiss doesn't race with the click that opened something else) and
 * ignores the event if it landed inside `ref`'s element or was already
 * handled by something else.
 */
export default function useOutsideClick({
  ref,
  handler,
}: UseOutsideClickOptions) {
  useEffect(() => {
    const listener = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (
        event.defaultPrevented ||
        !ref.current ||
        !target ||
        ref.current.contains(target)
      ) {
        return;
      }
      handler(event);
    };

    document.addEventListener('pointerdown', listener, true);
    return () => {
      document.removeEventListener('pointerdown', listener, true);
    };
  }, [ref, handler]);
}
